import React from 'react';
import Immutable from 'immutable';
import transit from 'transit-immutable-js';
import { getPhrase, getFormItemLabel, getColumnText } from '../../data/phrases';
import Form from '../../components/definitions/forms/formdefinition';
import FormItem from '../../components/definitions/forms/formitemdefinition';
import { processFilter } from './utils';
import Icon from '../../components/icon';


const EMPTY_SET = Immutable.Set([]);
const EMPTY_LIST = Immutable.List([]);
const EMPTY_MAP = Immutable.Map({});
const EMPTY_STATE = Immutable.fromJS({
	__module: 'AdvancedSearchForm',
	data: {
	}
});

const DEFAULT_DATA = Immutable.Map({
	vertical: true
});

let queryIdSequence = 0;
function getNewQueryId() {
	queryIdSequence++;
	if (queryIdSequence > 999) {
		queryIdSequence = 1;
	}
	return (new Date().getTime()) * 1000 + queryIdSequence;
}

function createNodeItemMaps(nodes, formItemMap, outputMap) {
	if (!nodes) {
		return;
	}
	const processNode = function(node) {
		const name = node.get('name');
		const formItem = node.get('formItem');
		const output = node.get('output');
		const nodes = node.get('nodes');
		if (formItem) {
			formItemMap[name] = formItem;
		}
		if (output) {
			outputMap[name] = output;
		}
		if (nodes) {
			nodes.forEach(node => processNode(node));
		}
	};

	nodes.forEach(node => processNode(node));
}

function createFormDefinition(formItems, readOnly) {
	formItems.forEach(item => item.setReadOnly(readOnly.get(item.getName())));
	return Form().setVirtual(false).setActionMode('all').setItems(formItems);
}

const EMPTY_FORM_DEFINITION = Form().setActionMode('all').setItems([]);

export default class AdvancedSearchFormModel {
	#data;

	constructor(data) {
		this.#data = data || DEFAULT_DATA;
	}

	getData() {
		return this.#data;
	}

	setLoadQueries(func) {
		return new AdvancedSearchFormModel(this.#data.set('loadQueries', func));
	}

	setSaveQueries(func) {
		return new AdvancedSearchFormModel(this.#data.set('saveQueries', func));
	}

	isLoaded() {
		return !!this.#data.get('queries');
	}

	setDefaultQueryId(value) {
		return new AdvancedSearchFormModel(this.#data.set('defaultQueryId', value));
	}

	getFilter() {
		let fullFilter = (this.#data.get('filter') || EMPTY_MAP).toJS();
		let filter = {};
		const _isNull = fullFilter._isNull;
		this.getFormDefinition().getItems().forEach(formItem => {
			const name = formItem.getName();
			if (_isNull && _isNull[name]) {
				return;
			}
			filter[name] = fullFilter[name];
		});
		const definition = this.getFormDefinition();
		processFilter(definition, filter);
		filter._output = this.getSelectedOutputColumns().toArray();
		filter._negation = fullFilter._negation;
		filter._isNull = _isNull;
		return filter;
	}

	getActiveFieldNameMap() {
		let res = {};
		this.getFormDefinition().getItems().forEach(formItem => {
			const name = formItem.getName();
			res[name] = true;
		});
		return res;
	}

	setInitialValues(values) {
		return new AdvancedSearchFormModel(this.#data.set('initialValues', Immutable.fromJS(values)));
	}

	setValue(name, value) {
		return new AdvancedSearchFormModel(this.#data.setIn(['filter', name], Immutable.fromJS(value)));
	}

	applyInitialValues() {
		const iniVals = this.#data.get('initialValues');
		if (!iniVals) {
			return this;
		}
		let afm = this.getActiveFieldNameMap();
		let model = this;

		iniVals.forEach(iniVal => {
			let names = iniVal.get('name');
			let value = iniVal.get('value');
			if (typeof names === 'string') {
				if (afm[names]) {
					model = model.setValue(names, value);
				}
			} else {
				for (let i=0; i<names.size; i++) {
					const name = names.get(i);
					if (afm[name]) {
						model = model.setValue(name, value);
						break;
					}
				}
			}
		});
		return model;
	}

	async initialize() {
		if (this.isLoaded()) {
			return this;
		}
		let model = await this.loadQueries();
		model = model.applyInitialValues();
		return model;
	}

	async loadQueries() {
		const loadQueries = this.#data.get('loadQueries');
		if (!loadQueries) {
			return this;
		}
		const defaultQueryId = this.#data.get('defaultQueryId');
		let res = await loadQueries(this);
		if (defaultQueryId) {
			res = res.setSelectedQueryId(defaultQueryId);
		}
		return res;
	}

	async saveQueries() {
		const saveQueries = this.#data.get('saveQueries');
		if (!saveQueries) {
			return this;
		}
		return saveQueries(this);
	}

	setQueries(value) {
		console.log('queries = ', value.toJS());
		return new AdvancedSearchFormModel(this.#data.set('queries', value));
	}

	setFilter(value) {
		return new AdvancedSearchFormModel(this.#data.set('filter', value || EMPTY_MAP));
	}

	clearFilter() {
		const readOnly = this.getReadOnly();
		const filter = (this.#data.get('filter') || EMPTY_MAP).filter((value, key) => readOnly.get(key));
		return new AdvancedSearchFormModel(this.#data.set('filter', filter));
	}

	setSelectedQueryId(queryId) {
		const queries = this.getQueries();
		if (queries.size === 0) {
			if (this.#data.get('selectedQuery')) {
				return new AdvancedSearchFormModel(this.#data.delete('selectedQuery'));
			}
			return this;
		}
		let selQuery = queries.find(q => q.get('id') == queryId);
		if (!selQuery) {
			selQuery = queries.get(0);
		}
		let model = new AdvancedSearchFormModel(this.#data.set('selectedQuery', selQuery));
		model = model.setSelectedNodes(selQuery.get('selected'), selQuery.get('selectedList'));
		model = model.setExpandedNodes(selQuery.get('expanded'));
		model = model.setFilter(selQuery.get('filter'));
		model = model.setReadOnly(selQuery.get('readOnly'));

		const onQuerySelected = this.#data.get('onQuerySelected');
		if (onQuerySelected) {
			onQuerySelected(queryId);
		}
		return model;
	}

	setOnQuerySelected(func) {
		return new AdvancedSearchFormModel(this.#data.set('onQuerySelected', func));
	}


	createQuery(id, name) {
		let data = this.#data;
		id = id || getNewQueryId();
		let query = Immutable.Map({
			id,
			filter: data.get('filter') || EMPTY_MAP,
			expanded: data.get('expandedNodes'),
			name,
			readOnly: data.get('readOnly'),
			selected: data.get('selectedNodes'),
			selectedList: data.get('selectedNodeList')
		});
		let queries = this.getQueries();
		const queryIndex = queries.findIndex(q => q.get('id') === id);
		if (queryIndex < 0) {
			queries = queries.push(query);
		} else {
			queries = queries.set(queryIndex, query);
		}
		data = data.set('queries', queries);
		return new AdvancedSearchFormModel(data).setSelectedQueryId(id);
	}

	getSelectedQuery() {
		return this.#data.get('selectedQuery');
	}

	toggleReadOnly(name) {
		let readOnly = this.getReadOnly();
		if (readOnly.get(name)) {
			readOnly = readOnly.delete(name);
		} else {
			readOnly = readOnly.set(name, true);
		}
		return this.setReadOnly(readOnly);
	}

	getReadOnly = function() {
		return this.#data.get('readOnly') || EMPTY_MAP;
	}

	setReadOnly = function(value) {
		const readOnly = value || EMPTY_MAP;
		let data = this.#data.set('readOnly', value);
		let formDefinition = this.getFormDefinition();
		if (formDefinition) {
			formDefinition.getItems().forEach(item => item.setReadOnly(readOnly.get(item.getName())));
		}
		return new AdvancedSearchFormModel(data);
	}

	getQueries() {
		return this.#data.get('queries') || EMPTY_LIST;
	}

	setQueriesFromTransit(value) {
		let queries;
		let model = this;
		if (value) {
			queries = transit.fromJSON(value) || EMPTY_LIST;
		} else {
			queries = EMPTY_LIST;
		}
		model = model.setQueries(queries);
		if (queries.size > 0) {
			model = model.setSelectedQueryId(queries.getIn([0, 'id']));
		}
		return model;
	}

	getQueriesForTransit() {
		return transit.toJSON(this.getQueries() || EMPTY_LIST);
	}

	getSelectedQueryId() {
		return this.#data.getIn(['selectedQuery', 'id']);
	}

	removeSelectedQuery() {
		const queryId = this.getSelectedQueryId();
		const queries = this.getQueries().filter(q => q.get('id') !== queryId);
		return new AdvancedSearchFormModel(this.#data.set('queries', queries)).setSelectedQueryId(null);
	}

	getFormId() {
		return this.#data.get('formId');
	}

	getNodes() {
		return this.#data.get('nodes') || EMPTY_LIST;
	}

	getSelectedNodes() {
		return this.#data.get('selectedNodes') || EMPTY_SET;
	}

	getSelectedNodeList() {
		return this.#data.get('selectedNodeList') || EMPTY_LIST;
	}

	getExpandedNodes() {
		return this.#data.get('expandedNodes') || EMPTY_SET;
	}


	getSelectedOutputColumns() {
		return this.#data.get('selectedOutputColumns') || EMPTY_LIST;
	}

	getFormDefinition() {
		return this.#data.get('formDefinition') || EMPTY_FORM_DEFINITION;
	}

	setSelectedNodes(selected, selectedList) {
		selected = selected || EMPTY_SET;
		if (!selectedList) {
			const origSelected = this.getSelectedNodes();
			const origSelectedList = this.getSelectedNodeList();
			selectedList = origSelectedList.filter(name => selected.has(name));
			selectedList = selectedList.concat(selected.filter(name => !origSelected.has(name)));
		}
		const outputMap = this.#data.get('outputMap') || EMPTY_MAP;
		const formItemMap = this.#data.get('formItemMap') || EMPTY_MAP;
		return new AdvancedSearchFormModel(
			this.#data
				.set('selectedNodes', selected)
				.set('selectedNodeList', selectedList)
				.set('selectedOutputColumns', selectedList.map(name => outputMap.get(name)).filter(Boolean))
				.set('formDefinition', createFormDefinition(selectedList.map(name => formItemMap.get(name)).filter(Boolean), this.getReadOnly()))
		);
	}

	setExpandedNodes(expanded) {
		return new AdvancedSearchFormModel(this.#data.set('expandedNodes', expanded));
	}

	setFieldTree(fieldTree) {
		let data = this.#data;
		const nodes = createNodes(fieldTree);
		data = data.set('nodes', nodes);
		let formItemMap = {}, outputMap = {};
		createNodeItemMaps(nodes, formItemMap, outputMap);
		data = data.set('formItemMap', Immutable.Map(formItemMap)).set('outputMap', Immutable.Map(outputMap));
		data = data.set('formId', fieldTree.formId);
		data = data.set('expandedNodes', Immutable.Set(['filter/', 'output/']));
		return new AdvancedSearchFormModel(data);
	}

	getSelectedTabId() {
		return this.#data.get('selectedTabId') || 'queries';
	}

	setSelectedTabId(value) {
		return new AdvancedSearchFormModel(this.#data.set('selectedTabId', value));
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}
}

function getOutputNodeName(src) {
	if (src.noOutput) {
		return null;
	}
	return 'o$' + (src.outputName || src.output || src.name);
}

function getNull() {
	return null;
}

function getLeftIconsFor(itemName, withNegation, withEmpty) {
	if (!withNegation && !withEmpty) {
		return getNull;
	}
	const isNullPath = ['_isNull', itemName];
	const negationPath = ['_negation', itemName];
	return function(ctx) {
		return (
			<React.Fragment>
				{
					withNegation && (
						<Icon
							name={ctx.getProperty(negationPath) ? 'notEqual' : 'equal'}
							active
							onMouseDown={e => {
								e.preventDefault();
								ctx.setProperty(negationPath, !ctx.getProperty(negationPath));
							}}
						/>
					)
				}
				{
					withEmpty && (
						<Icon
							name={ctx.getProperty(isNullPath) ? 'emptyOn' : 'emptyOff'}
							active
							onMouseDown={e => {
								e.preventDefault();
								ctx.setProperty(isNullPath, !ctx.getProperty(isNullPath));
							}}
						/>
					)
				}
			</React.Fragment>
		);
	};
}

function getNoInputFor(itemName) {
	const isNullPath = ['_isNull', itemName];
	return function(ctx) {
		return ctx.getProperty(isNullPath);
	};
}

function getRightIconsFor(itemName) {
	return function(ctx) {
		return (
			<Icon
				name="pencil"
				active
				onMouseDown={e => {
					e.preventDefault();
					ctx.setModel(ctx.getModel().toggleReadOnly(itemName))
				}}
			/>
		);
	};
}

function createFilterTree(fieldTree) {
	const entityName = fieldTree.entityName;

	const processFormItemNode = function(src) {
		if (!src.control) {
			return null;
		}
		const label = getFormItemLabel(src.name, entityName)
		return {
			name: src.name,
			text: label,
			formItem: FormItem(src.name, src.control)
						.setLookup(src.lookup)
						.setLabel(label)
						.setFixedParams(src.fixedParams)
						.setLeftIcons(getLeftIconsFor(src.name, fieldTree.formItemNegation, src.nullable))
						.setRightIcons(getRightIconsFor(src.name))
						.setNoInput(getNoInputFor(src.name))
						.setProcessForSearch(src.processValue),
			checkAlong: getOutputNodeName(src) || (src.checkAlong ? 'o$' + src.checkAlong : null)
		}
	};

	const processFolderNode = function(src) {
		return {
			name: src.name,
			text: getFormItemLabel(src.name, entityName),
			nodes: src.nodes.map(node => processNode(node)).filter(Boolean)
		};
	};

	const processNode = function(src) {
		return src.nodes ? processFolderNode(src) : processFormItemNode(src);
	};
	return fieldTree.root.map(node => processNode(node)).filter(Boolean);
}

function createOutputTree(fieldTree) {
	const entityName = fieldTree.entityName;

	const processOutputNode = function(src) {
		if (src.noOutput) {
			return null;
		}
		return {
			name: getOutputNodeName(src),
			text: getColumnText(src.output || src.name, entityName),
			output: src.output || src.name
		}
	};

	const processFolderNode = function(src) {
		return {
			name: 'o$' + src.name,
			text: getFormItemLabel(src.name, entityName),
			nodes: src.nodes.map(node => processNode(node)).filter(Boolean)
		};
	};

	const processNode = function(src) {
		return src.nodes ? processFolderNode(src) : processOutputNode(src);
	};
	return fieldTree.root.map(node => processNode(node)).filter(Boolean);

}

function createNodes(fieldTree) {
	return Immutable.fromJS([
		{
			name: 'filter',
			text: getPhrase('filter'),
			nodes: createFilterTree(fieldTree)
		},
		{
			name: 'output',
			text: getPhrase('output'),
			nodes: createOutputTree(fieldTree)
		}
	]);

};

